// src/pages/buyerNet/buyerNet.shared.js
import { useEffect } from "react";
import {
  getDaysInterestAmount,
  getMonthInsurance,
  getPrepaidMonthTaxes,
  getAnnualTaxforPrepaid,
  getMonthlyPMIAmount,
} from "../utils/calculations";

export const parseCurrency = (val) => {
  const n = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const toDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const defaultEst = toDateStr(addDays(new Date(), 30));

/**
 * ✅ Compute Commission (% ↔ $ conversion)
 * @param {Object} formData - Current form state
 * @param {Boolean} isPercentChanged - true if user changed %, false if user changed $
 * @returns {Object} Updated formData with recalculated commission
 */
export const computeCommission = (formData = {}, isPercentChanged = false) => {
  const sp = parseCurrency(formData?.salesPrice);
  if (!sp) return formData; // no valid sales price → no calculations

  const meta = structuredClone(formData?.compensationMeta ?? {});

  const sellerPercent = parseCurrency(meta.sellerBrokerCompensation);
  const buyerPercent = parseCurrency(meta.buyerBrokerCompensation);
  const sellerAmount = parseCurrency(meta.sellerBrokerCompensationAmount);
  const buyerAmount = parseCurrency(meta.buyerBrokerCompensationAmount);

  // --- Core logic ---
  if (isPercentChanged) {
    // % → $
    meta.sellerBrokerCompensationAmount = ((sp * sellerPercent) / 100).toFixed(2);
    meta.buyerBrokerCompensationAmount = ((sp * buyerPercent) / 100).toFixed(2);
  } else {
    // $ → %
    meta.sellerBrokerCompensation = sp > 0 ? ((sellerAmount / sp) * 100).toFixed(2) : "0.00";
    meta.buyerBrokerCompensation = sp > 0 ? ((buyerAmount / sp) * 100).toFixed(2) : "0.00";
  }

  // --- Total Commission ---
  const total =
    parseCurrency(meta.sellerBrokerCompensationAmount) +
    parseCurrency(meta.buyerBrokerCompensationAmount);

  meta.totalAgtAmount = total.toFixed(2);

  // --- Merge computed result back ---
  return {
    ...formData,
    compensationMeta: meta,
    totalClosingMeta: {
      ...(formData.totalClosingMeta ?? {}),
      totalCommission: total.toFixed(2),
    },
  };
};

/**
 * Preserve address-related flags & meta when replacing formData
 */
export const preserveAddressFields = (prev = {}, updated = {}) => {
  const result = { ...updated };

  if (updated.addressVerified === undefined) {
    result.addressVerified = prev.addressVerified ?? false;
  }

  result.addressMeta = {
    ...(prev.addressMeta || {}),
    ...(updated.addressMeta || {}),
  };

  result.loanInfoMeta = {
    ...(prev.loanInfoMeta || {}),
    ...(updated.loanInfoMeta || {}),
  };

  return result;
};

/**
 * Custom hook: handle all prepaid-related calculations
 * (Days Interest, Insurance, Taxes, PMI)
 */
export const usePrepaidEffects = (
  formData,
  setFormData,
  isEditLoading,
  setValue
) => {
  // Days Interest Amount
  useEffect(() => {
    if (isEditLoading) return;
    const daysInterestAmount = getDaysInterestAmount(
      parseCurrency(formData?.loanInfoMeta?.amount),
      parseCurrency(formData?.loanInfoMeta?.adjusted),
      formData?.loanInfoMeta?.interestRate1,
      formData?.prepaidsMeta?.daysInterest
    );

    setFormData((prev) => {
      if (prev.prepaidsMeta?.daysInterestAmount === daysInterestAmount)
        return prev;
      return {
        ...prev,
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          daysInterestAmount: daysInterestAmount,
        },
      };
    });
  }, [
    formData?.prepaidsMeta?.daysInterest,
    formData?.loanInfoMeta?.adjusted,
    formData?.loanInfoMeta?.interestRate1,
    formData?.loanInfoMeta?.amount,
    isEditLoading,
    setFormData,
  ]);

  // Insurance Amount
  useEffect(() => {
    if (isEditLoading) return;

    const insAmount = getMonthInsurance(
      parseCurrency(formData?.salesPrice),
      formData?.prepaidsMeta?.rateThou,
      formData?.prepaidsMeta?.monthsIns,
      formData?.loanInfoMeta?.buyersLoanType
    );

    setFormData((prev) => {
      if (prev.prepaidsMeta?.insAmount === insAmount) return prev;
      return {
        ...prev,
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          insAmount: insAmount,
        },
      };
    });
  }, [
    formData?.prepaidsMeta?.monthsIns,
    formData?.prepaidsMeta?.rateThou,
    formData?.salesPrice,
    formData?.loanInfoMeta?.buyersLoanType,
    isEditLoading,
    setFormData,
  ]);

  // prepaidsMeta.taxesAmount — single source; respects useForPrepaidCheck + annual tax
  useEffect(() => {
    if (isEditLoading) return;

    setFormData((prev) => {
      let raw;
      if (prev.loanInfoMeta?.buyersLoanType === "Cash") {
        raw = 0;
      } else if (prev.loanInfoMeta?.useForPrepaidCheck === true) {
        raw = getAnnualTaxforPrepaid(prev);
      } else {
        raw = getPrepaidMonthTaxes(
          parseCurrency(prev.salesPrice),
          prev.prepaidsMeta?.rate,
          prev.prepaidsMeta?.monthsTaxes
        );
      }
      const taxesAmount = Number(raw ?? 0).toFixed(2);
      if (prev.prepaidsMeta?.taxesAmount === taxesAmount) return prev;
      return {
        ...prev,
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          taxesAmount,
        },
      };
    });
  }, [
    formData?.loanInfoMeta?.useForPrepaidCheck,
    formData?.loanInfoMeta?.annualPropertyTax,
    formData?.loanInfoMeta?.buyersLoanType,
    formData?.salesPrice,
    formData?.prepaidsMeta?.rate,
    formData?.prepaidsMeta?.monthsTaxes,
    isEditLoading,
    setFormData,
  ]);

  // Keep react-hook-form in sync when taxesAmount is set from effects (field uses defaultValue + RHF)
  useEffect(() => {
    if (isEditLoading || typeof setValue !== "function") return;
    const v = formData?.prepaidsMeta?.taxesAmount;
    if (v === undefined || v === null) return;
    setValue("prepaidsMeta.taxesAmount", v, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [formData?.prepaidsMeta?.taxesAmount, isEditLoading, setValue]);

  // PMI calculations — only recompute here once the user has manually overridden the
  // rate; otherwise getTitleEscrowData's per-loan-type calculation already owns this.
  useEffect(() => {
    if (isEditLoading) return;
    if (formData?.paymentMeta?.rateMonthlyPMIManual !== true) return;

    const noPMI = formData?.loanInfoMeta?.noPMICheck;

    const dataTwoMonthsPMI = noPMI
      ? { rateMonthlyPMIAmount: "0.00", twoMonthsPMI: "0.00" }
      : getMonthlyPMIAmount(formData);

    setFormData((prev) => {
      if (
        prev.paymentMeta?.rateMonthlyPMIAmount ===
        dataTwoMonthsPMI.rateMonthlyPMIAmount &&
        prev.prepaidsMeta?.twoMonthsPMI === dataTwoMonthsPMI.twoMonthsPMI
      ) {
        return prev;
      }
      return {
        ...prev,
        paymentMeta: {
          ...(prev.paymentMeta || {}),
          rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
        },
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData?.paymentMeta?.rateMonthlyPMI,
    formData?.paymentMeta?.rateMonthlyPMIManual,
    formData?.loanInfoMeta?.noPMICheck,
    formData?.loanInfoMeta?.amount,
    formData?.loanInfoMeta?.amount2,
    isEditLoading,
    setFormData,
  ]);
};